import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  VStack,
  Text,
  useToast,
  Heading,
  Select,
  Alert,
  AlertIcon,
  Code,
  Divider,
} from '@chakra-ui/react';
import { supabase } from '../../supabaseClient';

const SecretKeyDebugger = () => {
  const [labs, setLabs] = useState([]);
  const [selectedLabId, setSelectedLabId] = useState('');
  const [newSecretKey, setNewSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState([]);
  const toast = useToast();

  const addDebugInfo = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugInfo(prev => [...prev, { timestamp, message, type }]);
  };

  useEffect(() => {
    fetchLabs();
  }, []);

  const fetchLabs = async () => {
    try {
      addDebugInfo('Fetching labs from database...');
      const { data, error } = await supabase
        .from('labs')
        .select('*')
        .order('name');

      if (error) {
        addDebugInfo(`Error fetching labs: ${error.message}`, 'error');
        throw error;
      }

      addDebugInfo(`Successfully fetched ${data.length} labs`);
      addDebugInfo(`Labs data: ${JSON.stringify(data.map(lab => ({ id: lab.id, name: lab.name, secret_key: lab.secret_key })))}`, 'info');
      setLabs(data || []);
    } catch (error) {
      addDebugInfo(`Failed to fetch labs: ${error.message}`, 'error');
      toast({
        title: 'Error fetching labs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const testSecretKeyUpdate = async () => {
    if (!selectedLabId || !newSecretKey.trim()) {
      addDebugInfo('Please select a lab and enter a secret key', 'warning');
      return;
    }

    setLoading(true);
    addDebugInfo(`Starting secret key update for lab ID: ${selectedLabId}`);
    addDebugInfo(`New secret key: "${newSecretKey}"`);

    try {
      // Test if the labs table exists and has secret_key column
      addDebugInfo('Testing table structure...');
      const { data: tableTest, error: tableError } = await supabase
        .from('labs')
        .select('id, name, secret_key')
        .eq('id', selectedLabId)
        .single();

      if (tableError) {
        addDebugInfo(`Table structure test failed: ${tableError.message}`, 'error');
        throw tableError;
      }

      addDebugInfo(`Current lab data: ${JSON.stringify(tableTest)}`);

      // Perform the update
      addDebugInfo('Performing secret key update...');
      const { data, error } = await supabase
        .from('labs')
        .update({ secret_key: newSecretKey })
        .eq('id', selectedLabId)
        .select();

      if (error) {
        addDebugInfo(`Update failed: ${error.message}`, 'error');
        throw error;
      }

      addDebugInfo(`Update successful! Response data: ${JSON.stringify(data)}`);

      // Verify the update
      addDebugInfo('Verifying update...');
      const { data: verifyData, error: verifyError } = await supabase
        .from('labs')
        .select('id, name, secret_key')
        .eq('id', selectedLabId)
        .single();

      if (verifyError) {
        addDebugInfo(`Verification failed: ${verifyError.message}`, 'error');
      } else {
        addDebugInfo(`Verification successful: ${JSON.stringify(verifyData)}`);
        if (verifyData.secret_key === newSecretKey) {
          addDebugInfo('✅ Secret key was successfully updated in database!', 'success');
        } else {
          addDebugInfo('❌ Secret key in database does not match what we tried to set', 'error');
        }
      }

      // Refresh labs list
      await fetchLabs();

      toast({
        title: 'Test completed',
        description: 'Check debug information below',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      addDebugInfo(`Test failed: ${error.message}`, 'error');
      toast({
        title: 'Test failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const clearDebugInfo = () => {
    setDebugInfo([]);
  };

  const selectedLab = labs.find(lab => lab.id.toString() === selectedLabId);

  return (
    <Box p={6} maxW="800px" mx="auto">
      <Heading size="lg" mb={4}>Secret Key Update Debugger</Heading>
      
      <Alert status="info" mb={4}>
        <AlertIcon />
        This tool helps debug secret key updates by showing detailed information about database operations.
      </Alert>

      <VStack spacing={4} align="stretch">
        <Box>
          <Text fontWeight="bold" mb={2}>Select Lab:</Text>
          <Select 
            value={selectedLabId} 
            onChange={(e) => setSelectedLabId(e.target.value)}
            placeholder="Choose a lab to test"
          >
            {labs.map(lab => (
              <option key={lab.id} value={lab.id}>
                {lab.name} {lab.secret_key ? `(Current key: ${lab.secret_key})` : '(No key set)'}
              </option>
            ))}
          </Select>
        </Box>

        {selectedLab && (
          <Box p={3} bg="gray.50" borderRadius="md">
            <Text><strong>Selected Lab:</strong> {selectedLab.name}</Text>
            <Text><strong>Lab ID:</strong> {selectedLab.id}</Text>
            <Text><strong>Current Secret Key:</strong> {selectedLab.secret_key || 'None'}</Text>
          </Box>
        )}

        <Box>
          <Text fontWeight="bold" mb={2}>New Secret Key:</Text>
          <Input
            value={newSecretKey}
            onChange={(e) => setNewSecretKey(e.target.value)}
            placeholder="Enter new secret key to test"
          />
        </Box>

        <Button
          colorScheme="blue"
          onClick={testSecretKeyUpdate}
          isLoading={loading}
          loadingText="Testing..."
          isDisabled={!selectedLabId || !newSecretKey.trim()}
        >
          Test Secret Key Update
        </Button>

        <Button size="sm" onClick={clearDebugInfo}>
          Clear Debug Info
        </Button>

        <Divider />

        <Box>
          <Heading size="md" mb={3}>Debug Information:</Heading>
          <Box maxH="400px" overflowY="auto" p={3} bg="black" color="green.300" borderRadius="md" fontFamily="mono">
            {debugInfo.length === 0 ? (
              <Text color="gray.400">No debug information yet. Run a test to see results.</Text>
            ) : (
              debugInfo.map((info, index) => (
                <Box key={index} mb={1}>
                  <Text as="span" color="gray.400">[{info.timestamp}]</Text>{' '}
                  <Text as="span" color={
                    info.type === 'error' ? 'red.300' : 
                    info.type === 'success' ? 'green.300' : 
                    info.type === 'warning' ? 'yellow.300' : 
                    'white'
                  }>
                    {info.message}
                  </Text>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </VStack>
    </Box>
  );
};

export default SecretKeyDebugger; 